import { Panel } from "primereact/panel";
import { Tag } from "primereact/tag";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import { PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS } from "../../../../permissions";
import slotChangeRequestsAPI from "../../../../services/slot_change_requests";
import {
  formatPreferredSlots,
  formatRequestDate,
  formatSlotLabel,
  statusTag,
  urgencyTag,
} from "../../slot-change-requests/slotChangeUtils";

const StudentSlotChangeRequestsPanelContent = ({
  studentId,
  schoolId,
  trainingId,
  className,
  header,
}) => {
  const requestsQuery = slotChangeRequestsAPI.useGetList(
    schoolId,
    trainingId,
    "",
    1,
    100,
    { student_id: studentId, status: "all", urgency: "all" }
  );
  const requests = requestsQuery?.data?.data || [];
  const requestsTotal = requestsQuery?.data?.total ?? requests.length;

  const renderFromClass = (item) => {
    const fromClass = item?.from_class;
    const fromSlot = item?.from_slot;
    if (!fromClass && !fromSlot) {
      return "-";
    }
    return (
      <div className="flex flex-column">
        <span>{fromClass?.name || "-"}</span>
        {fromSlot && <small className="text-500">{formatSlotLabel(fromSlot)}</small>}
      </div>
    );
  };

  const panelHeader = `${header} (${requestsTotal})`;
  return (
    <Panel header={panelHeader} toggleable className={className}>
      {requestsQuery?.isLoading && (
        <div className="py-4 text-center text-500">Chargement...</div>
      )}
      {requestsQuery?.isError && (
        <div className="py-2 text-center text-red-500">
          {requestsQuery?.error?.message ||
            "Impossible de récupérer les demandes de changement de créneaux."}
        </div>
      )}
      {!requestsQuery?.isLoading && !requestsQuery?.isError && (
        requests.length === 0 ? (
          <div className="py-3 text-center text-500">Aucune demande de changement de créneau.</div>
        ) : (
          <div className="flex flex-column gap-2" data-testid="slot-change-requests-cards">
            {requests.map((item, index) => (
              <div
                key={item?.id || index}
                className="border-1 surface-border border-round p-3 flex flex-column gap-2"
                data-testid={`slot-change-request-card-${item?.id || index}`}
              >
                <div className="flex justify-content-between align-items-start gap-2">
                  <div className="flex flex-wrap gap-1">
                    <Tag {...statusTag(item?.status)} />
                    <Tag {...urgencyTag(item?.urgency)} />
                  </div>
                  <small className="text-600 text-right">{formatRequestDate(item?.requested_at)}</small>
                </div>
                <div className="flex flex-column gap-2 text-sm">
                  <div>
                    <div className="text-500 font-medium">Classe actuelle</div>
                    {renderFromClass(item)}
                  </div>
                  <div>
                    <div className="text-500 font-medium">Créneaux souhaités</div>
                    <span className="text-900">{formatPreferredSlots(item)}</span>
                  </div>
                </div>
                {item?.reason && (
                  <div className="text-900 border-top-1 surface-border pt-2" style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>
                    {item.reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </Panel>
  );
};

const StudentSlotChangeRequestsPanel = ({
  studentId,
  schoolId,
  trainingId,
  className,
  header = "Demandes de changement de créneaux",
}) => {
  const { hasPermission } = useAuthContext();

  if (!studentId || !hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.LIST)) {
    return null;
  }

  return (
    <StudentSlotChangeRequestsPanelContent
      studentId={studentId}
      schoolId={schoolId}
      trainingId={trainingId}
      className={className}
      header={header}
    />
  );
};

export default StudentSlotChangeRequestsPanel;